import { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { CustomBranding } from '../types';

export interface HostBrandingResult {
  branding: CustomBranding | null;
  loading: boolean;
  isPremium: boolean; // Host has active Party Pass or Pro subscription
}

const toDate = (v: any): Date | undefined => {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();
  return undefined;
};

const isActive = (sub: any | undefined) => {
  if (!sub?.active) return false;
  const expires = toDate(sub.expiresAt);
  return !!expires && expires.getTime() > Date.now();
};

/**
 * Loads the host user's branding if their account has an active Party Pass or Pro Subscription
 * and customBranding.enabled is true.
 */
export const useHostBranding = (hostUserId?: string | null): HostBrandingResult => {
  const [branding, setBranding] = useState<CustomBranding | null>(null);
  const [loading, setLoading] = useState<boolean>(!!hostUserId);
  const [isPremium, setIsPremium] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!hostUserId) { setBranding(null); setLoading(false); return; }
      if (!auth.currentUser) { setBranding(null); setIsPremium(false); setLoading(false); return; }
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'users', hostUserId));
        if (!snap.exists()) { if (!cancelled) { setBranding(null); } return; }
        const data: any = snap.data();
        const premium = isActive(data.partyPass) || isActive(data.proSubscription);
        const brand: CustomBranding | undefined = data.customBranding;
        const enabled = !!brand?.enabled;
        if (!cancelled) {
          setIsPremium(!!premium);
          setBranding(premium && enabled ? brand! : null);
        }
      } catch (e) {
        if (!cancelled) { setBranding(null); setIsPremium(false); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [hostUserId]);

  return { branding, loading, isPremium };
};
